// createApiClient — factory for typed, tenant-scoped HTTP clients.
//
// Spec: Epic FE-US-INT-01 §4.4. Each backend service (Pricing, Quotation,
// Accounts, Billing, Notification) gets its own client built from this
// factory. The factory owns:
//   - Header injection (Authorization, x-tenant-id, x-request-id,
//     x-actor-user-id, Accept-Language)
//   - Timeout (default 15s; configurable per-client and per-request)
//   - Retry on 5xx (3 attempts total, exponential backoff)
//   - 401 callback (consumer wires this to "redirect to /login")
//   - JSON parsing
//   - Optional Zod parsing at the boundary — any ZodError surfaces as
//     ApiError { kind: 'validation' }
//
// This module does NOT depend on react-router, the auth provider, or any
// country code. Consumers wire it in their own lib/apiClient.ts.

import type { ZodType } from 'zod';

import type { ApiError, ProblemDetails } from './apiError';
import { generateRequestId } from './correlationId';

export interface ApiClientConfig {
  /** Service base URL — e.g. `http://localhost:3012`. No trailing slash required. */
  baseUrl: string;
  /** Returns the current Bearer token, or null when unauthenticated. */
  getAuthToken: () => string | null;
  /** Returns the current tenant id. Required on every request. */
  getTenantId: () => string;
  /** Returns the acting user's id, or null when unauthenticated. */
  getActorUserId: () => string | null;
  /**
   * Returns the ISO-3166 country code for the current app surface (e.g. 'US').
   * Pricing service strictly validates the `x-country-code` header; without
   * it requests are rejected with HTTP 400. Defaults to () => 'US' when
   * omitted so the option is backwards-compat.
   */
  getCountryCode?: () => string;
  /**
   * Send ONLY `Authorization`, `Accept` and `Content-Type` — suppressing the
   * `x-tenant-id` / `x-country-code` / `x-request-id` / `x-actor-user-id`
   * context headers.
   *
   * Default `false`: every backend service expects those headers, and several
   * reject the request without them.
   *
   * Set it when the target is NOT one of our backend services but a separate
   * APPLICATION on another origin — today, the Customer Portal's BFF, which the
   * CRM calls for portal-invite status. Two reasons:
   *
   * 1. **They mean nothing there.** That app derives its own tenant from its
   *    server env and reads only `Authorization`. Sending our internal tenant,
   *    actor and correlation ids just leaks identifiers into another
   *    application's logs.
   * 2. **Every custom header must be pre-approved by CORS.** A cross-origin
   *    preflight fails on the FIRST header the other side does not list, and an
   *    ingress with a default `cors-allow-headers` will not list our `x-*`
   *    ones. `Authorization` and `Content-Type` are on essentially every
   *    default list, so a request limited to them survives a CORS config we do
   *    not control. This turned a working feature into a silently blocked one
   *    in a deployed environment.
   */
  omitContextHeaders?: boolean;
  /** Called once when any request returns 401. Consumer redirects to /login. */
  onUnauthorized?: () => void;
  /** Per-client default timeout. Falls back to 15_000ms. */
  defaultTimeoutMs?: number;
  /** Override retry count for 5xx (default 3 total attempts). */
  maxRetries?: number;
  /**
   * Test seam: wait function used between retries. Production uses real
   * setTimeout-based wait; tests inject a fake timer.
   */
  sleep?: (ms: number) => Promise<void>;
  /**
   * Test seam: alternate fetch impl. Defaults to `globalThis.fetch`.
   */
  fetchImpl?: typeof fetch;
}

export interface RequestOptions<T> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Per-request timeout override. */
  timeoutMs?: number;
  /**
   * Optional Zod schema. When provided, the response JSON is parsed through
   * the schema; any ZodError surfaces as ApiError { kind: 'validation' }.
   */
  schema?: ZodType<T>;
}

export interface ApiClient {
  request<T>(path: string, options?: RequestOptions<T>): Promise<T>;
  get<T>(path: string, options?: Omit<RequestOptions<T>, 'method' | 'body'>): Promise<T>;
  post<T>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions<T>, 'method' | 'body'>,
  ): Promise<T>;
  put<T>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions<T>, 'method' | 'body'>,
  ): Promise<T>;
  patch<T>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions<T>, 'method' | 'body'>,
  ): Promise<T>;
  delete<T>(path: string, options?: Omit<RequestOptions<T>, 'method' | 'body'>): Promise<T>;
}

const DEFAULT_TIMEOUT_MS = 15_000;
const DEFAULT_MAX_RETRIES = 3;
const ACCEPT_LANGUAGE = 'en-US';

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function buildUrl(base: string, path: string, query?: RequestOptions<unknown>['query']): string {
  const trimmedBase = base.endsWith('/') ? base : base + '/';
  const trimmedPath = path.replace(/^\//, '');
  const url = new URL(trimmedPath, trimmedBase);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

/**
 * Parses an error-body Problem Details payload. Tolerant: a server may
 * return plain text, a non-JSON envelope, or nothing at all.
 */
async function parseProblem(res: Response): Promise<ProblemDetails | undefined> {
  const text = await res.text().catch(() => '');
  if (!text) return undefined;
  try {
    const parsed: unknown = JSON.parse(text);
    if (parsed && typeof parsed === 'object') return parsed as ProblemDetails;
    return { detail: String(parsed) };
  } catch {
    return { detail: text };
  }
}

function classifyHttpError(status: number, problem: ProblemDetails | undefined): ApiError {
  if (status === 401 || status === 403) {
    return { kind: 'auth', status: status as 401 | 403, problem };
  }
  if (status >= 400 && status < 500) {
    // problem must be present for `client` per the union; synthesize if missing.
    return { kind: 'client', status, problem: problem ?? { status, detail: `HTTP ${status}` } };
  }
  return { kind: 'server', status, problem };
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  const {
    baseUrl,
    getAuthToken,
    getTenantId,
    getActorUserId,
    // Optional; the request builder references `getCountryCode` directly, so it
    // MUST be destructured here — without it, ES-module strict mode throws
    // `ReferenceError: getCountryCode is not defined` on every request.
    getCountryCode,
    // Same destructuring requirement as `getCountryCode` above — the request
    // builder references it directly.
    omitContextHeaders = false,
    onUnauthorized,
    defaultTimeoutMs = DEFAULT_TIMEOUT_MS,
    maxRetries = DEFAULT_MAX_RETRIES,
    sleep = defaultSleep,
    fetchImpl,
  } = config;

  const doFetch: typeof fetch = fetchImpl ?? ((...args) => globalThis.fetch(...args));

  async function request<T>(path: string, options: RequestOptions<T> = {}): Promise<T> {
    const timeoutMs = options.timeoutMs ?? defaultTimeoutMs;
    const method = options.method ?? 'GET';
    const url = buildUrl(baseUrl, path, options.query);

    let attempt = 0;
    let lastError: ApiError | null = null;

    while (attempt < Math.max(1, maxRetries)) {
      attempt += 1;

      const controller = new AbortController();
      const timeoutHandle = setTimeout(() => controller.abort(), timeoutMs);
      // Compose external signal with our timeout signal.
      const externalSignal = options.signal;
      const onExternalAbort = (): void => controller.abort();
      if (externalSignal) externalSignal.addEventListener('abort', onExternalAbort);

      // Build headers. x-request-id is regenerated per attempt so that each
      // retried wire-attempt is correlated independently in backend logs.
      // `Accept` / `Accept-Language` are CORS-safelisted, so they never need a
      // cross-origin allow-list entry and are kept in both modes.
      const headers: Record<string, string> = {
        Accept: 'application/json',
        'Accept-Language': ACCEPT_LANGUAGE,
        ...(omitContextHeaders
          ? {}
          : {
              'x-tenant-id': getTenantId(),
              'x-country-code': (getCountryCode ?? (() => 'US'))(),
              'x-request-id': generateRequestId(),
            }),
        ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...options.headers,
      };
      const token = getAuthToken();
      if (token) headers.Authorization = `Bearer ${token}`;
      if (!omitContextHeaders) {
        const actorId = getActorUserId();
        if (actorId) headers['x-actor-user-id'] = actorId;
      }

      let res: Response;
      try {
        res = await doFetch(url, {
          method,
          headers,
          body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
          // `same-origin` (not `include`): we use JWT-in-Authorization-header
          // for auth, not cookies. Sending `credentials: 'include'` forces the
          // browser to require `Access-Control-Allow-Credentials: true` on the
          // preflight response, which is a needless coupling for an
          // OAuth-style API. Keeps CORS simpler across all 5 backend services.
          credentials: 'same-origin',
          signal: controller.signal,
        });
      } catch (err: unknown) {
        clearTimeout(timeoutHandle);
        if (externalSignal) externalSignal.removeEventListener('abort', onExternalAbort);
        // Distinguish timeout vs network. AbortError fires for both abort()
        // (our timeout) and external cancel. If the external signal aborted,
        // surface as network — only OUR controller signals timeout.
        if (controller.signal.aborted && !externalSignal?.aborted) {
          lastError = { kind: 'timeout', message: 'Request timed out', timeoutMs };
        } else if (externalSignal?.aborted) {
          lastError = { kind: 'network', message: 'Request was cancelled' };
          throw lastError;
        } else {
          const message = err instanceof Error ? err.message : 'Network error';
          lastError = { kind: 'network', message };
        }
        // Network/timeout — retry if attempts left.
        if (attempt < maxRetries) {
          await sleep(backoffMs(attempt));
          continue;
        }
        throw lastError;
      }

      clearTimeout(timeoutHandle);
      if (externalSignal) externalSignal.removeEventListener('abort', onExternalAbort);

      // 401 — invoke callback exactly once per request lifecycle, then
      // surface as ApiError (no retry on auth failures).
      if (res.status === 401) {
        onUnauthorized?.();
      }

      if (!res.ok) {
        const problem = await parseProblem(res);
        const err = classifyHttpError(res.status, problem);
        lastError = err;
        // Retry on server (5xx) only. Client/auth errors are terminal.
        if (err.kind === 'server' && attempt < maxRetries) {
          await sleep(backoffMs(attempt));
          continue;
        }
        throw err;
      }

      // Success path.
      if (res.status === 204) {
        return undefined as unknown as T;
      }

      let data: unknown;
      try {
        data = await res.json();
      } catch {
        // 2xx with non-JSON body — treat as undefined.
        return undefined as unknown as T;
      }

      const schema = options.schema;
      if (schema) {
        const result = schema.safeParse(data);
        if (!result.success) {
          throw { kind: 'validation', zodError: result.error } satisfies ApiError;
        }
        return result.data;
      }
      return data as T;
    }

    // Loop exited without return — exhausted retries.
    throw lastError ?? { kind: 'network', message: 'Request failed' };
  }

  return {
    request,
    get<T>(path: string, opts?: Omit<RequestOptions<T>, 'method' | 'body'>): Promise<T> {
      return request<T>(path, { ...opts, method: 'GET' } as RequestOptions<T>);
    },
    post<T>(
      path: string,
      body?: unknown,
      opts?: Omit<RequestOptions<T>, 'method' | 'body'>,
    ): Promise<T> {
      return request<T>(path, { ...opts, method: 'POST', body } as RequestOptions<T>);
    },
    put<T>(
      path: string,
      body?: unknown,
      opts?: Omit<RequestOptions<T>, 'method' | 'body'>,
    ): Promise<T> {
      return request<T>(path, { ...opts, method: 'PUT', body } as RequestOptions<T>);
    },
    patch<T>(
      path: string,
      body?: unknown,
      opts?: Omit<RequestOptions<T>, 'method' | 'body'>,
    ): Promise<T> {
      return request<T>(path, { ...opts, method: 'PATCH', body } as RequestOptions<T>);
    },
    delete<T>(path: string, opts?: Omit<RequestOptions<T>, 'method' | 'body'>): Promise<T> {
      return request<T>(path, { ...opts, method: 'DELETE' } as RequestOptions<T>);
    },
  };
}

/** Exponential backoff: 100ms, 300ms, 700ms, ... */
function backoffMs(attempt: number): number {
  return Math.min(2_000, 100 * (2 ** attempt - 1));
}
